import { useState } from 'react';

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('all');

  const categories = [
    { id: 'all', label: 'All Questions', icon: 'ri-list-check' },
    { id: 'pricing', label: 'Pricing & Value', icon: 'ri-money-dollar-circle-line' },
    { id: 'process', label: 'Process & Speed', icon: 'ri-speed-line' },
    { id: 'regional', label: 'Regional Specifics', icon: 'ri-map-pin-line' },
    { id: 'general', label: 'General', icon: 'ri-question-line' }
  ];

  const faqs = [
    // Pricing & Value Category
    {
      category: 'pricing',
      question: 'How much does it cost to replace windows in a 2,500 sq. ft. house in Virginia?',
      answer: 'Window replacement costs in Virginia typically range from $8,000 to $25,000 for a 2,500 sq. ft. home, depending on the number of windows, style, and features. With SnapWindows.ai, you can get an accurate, personalized Proposal in minutes by simply uploading photos—no waiting for a salesperson to visit your home.'
    },
    {
      category: 'pricing',
      question: 'Is it better to replace all windows at once or one at a time?',
      answer: 'Replacing all windows at once is usually more cost-effective. You save on labor costs, get consistent energy efficiency throughout your home, and often qualify for better bulk pricing. However, if budget is a concern, we can help you prioritize which windows need replacement first based on condition and energy loss.'
    },
    {
      category: 'pricing',
      question: 'What are the best window brands for Virginia\'s humidity and temperature swings?',
      answer: 'For Virginia\'s climate with hot, humid summers and cold winters, we recommend Alside ProSeries windows. They feature Low-E coating and argon gas insulation that handles temperature swings efficiently, plus they\'re designed to resist moisture damage from humidity. Their lifetime warranty including glass breakage gives you peace of mind.'
    },
    // Process & Speed Category
    {
      category: 'process',
      question: 'Can I get a window replacement Proposal online without a home visit?',
      answer: 'Yes! That\'s exactly what SnapWindows.ai was built for. Simply upload photos of your windows from your phone, and our AI technology provides an accurate Proposal within minutes. No 2-hour sales pitch, no pressure, no stranger in your home. You only meet us when you\'re ready for final measurements and installation.'
    },
    {
      category: 'process',
      question: 'How long does it take to get windows installed in Virginia right now?',
      answer: 'Current lead times in Virginia are typically 4-6 weeks from order to installation. Once your windows arrive, most residential projects take 1-3 days to complete depending on the number of windows. We\'ll give you an exact timeline with your quote so you can plan accordingly.'
    },
    {
      category: 'process',
      question: 'What is the difference between retrofit and full-frame window replacement?',
      answer: 'Retrofit (or insert) replacement keeps your existing window frame and installs a new window inside it—faster and less expensive. Full-frame replacement removes everything including the frame, ideal when there\'s rot or damage. We\'ll assess your situation from your photos and recommend the best approach for your home.'
    },
    // Regional Specifics Category
    {
      category: 'regional',
      question: 'Do I need a permit for window replacement in my county?',
      answer: 'Permit requirements vary by location in Virginia. Most retrofit replacements don\'t require permits, but full-frame replacements or changes to window size often do. We handle all necessary permits for Henrico, Richmond, Glen Allen, Short Pump, and surrounding areas—it\'s included in our service.'
    },
    {
      category: 'regional',
      question: 'Will new windows actually lower my Dominion Energy bill?',
      answer: 'Absolutely. Energy Star certified windows like Alside ProSeries can reduce energy loss by 25-30%. Virginia homeowners typically see $200-$400 annual savings on their Dominion Energy bills. The Low-E coating reflects heat in summer and retains warmth in winter, making your HVAC system work less.'
    },
    {
      category: 'regional',
      question: 'Do you only serve Virginia?',
      answer: 'Virginia is our home base. We also serve select nearby areas and expand carefully based on install capacity. Use the proposal form—if you\'re outside our service range, we\'ll tell you up front.'
    },
    // General Category (Original FAQs)
    {
      category: 'general',
      question: 'Do I need to be home for the Proposal?',
      answer: 'No! That\'s the beauty of our process. Simply upload photos of your windows from your phone, and we\'ll provide an ironclad Proposal within minutes. You only need to be home when our licensed production manager comes for final measurements and during installation.'
    },
    {
      category: 'general',
      question: 'What if my measurements change?',
      answer: 'Your online Proposal is based on AI measurements from photos, but we always send a licensed production manager to take final measurements before ordering. If there are any differences, we\'ll update your Proposal before manufacturing begins. No surprises.'
    },
    {
      category: 'general',
      question: 'Do you handle permits?',
      answer: 'Yes, we handle all necessary permits for your window replacement project. Permit requirements vary by location in Virginia, and we\'ll take care of everything to ensure your installation is fully compliant with local building codes.'
    },
    {
      category: 'general',
      question: 'What\'s included in the warranty?',
      answer: 'Every window comes with Alside\'s lifetime glass breakage warranty and manufacturer\'s warranty on all components. We also provide a workmanship guarantee on our installation. If anything goes wrong, we make it right.'
    },
    {
      category: 'general',
      question: 'How long does installation take?',
      answer: 'Most residential projects take 1-3 days depending on the number of windows. We\'ll give you an exact timeline with your Proposal. We work efficiently while maintaining the highest quality standards and respecting your home.'
    },
    {
      category: 'general',
      question: 'Are your photos really private?',
      answer: 'Absolutely. Your photos are encrypted at rest on our servers and never shared with third parties. We don\'t use cloud AI vendors or add your images to training datasets. Privacy is a core principle of how we operate.'
    },
    {
      category: 'general',
      question: 'What makes Alside windows different?',
      answer: 'Alside has been manufacturing premium windows for 72+ years. Their ProSeries windows are Energy Star certified, feature Low-E coating, argon gas insulation, and come with a lifetime glass breakage warranty. They\'re built to last and designed to save you money on energy bills.'
    },
    {
      category: 'general',
      question: 'Do you offer financing?',
      answer: 'Yes, we offer flexible financing options with approved credit. The best part? You don\'t pay anything until after installation is complete and you\'re Satisfied with the work. <strong>No money down</strong>, no pressure.'
    },
    {
      category: 'general',
      question: 'What areas do you serve?',
      answer: 'We serve Richmond, Glen Allen, Short Pump, Henrico, and surrounding areas in Virginia. If you\'re not sure if we cover your location, just enter your zip code in the Proposal form and we\'ll let you know.'
    },
    {
      category: 'general',
      question: 'Why should I choose SnapWindows over big national brands?',
      answer: 'Unlike national chains that send high-pressure salespeople to your home, we\'re a family-owned business focused on transparency and respect. You get the same premium Alside windows without the sales tactics, surprise pricing, or mandatory home visits. Plus, Billy and Dad personally manage every project.'
    }
  ];

  const filteredFaqs = activeCategory === 'all' 
    ? faqs 
    : faqs.filter(faq => faq.category === activeCategory);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const getCategoryColor = (categoryId: string) => {
    switch(categoryId) {
      case 'pricing': return 'from-[#D9A7B0] to-[#C8959F]';
      case 'process': return 'from-[#E8B4BC] to-[#D9A7B0]';
      case 'regional': return 'from-[#C8959F] to-[#B8858E]';
      default: return 'from-[#D9A7B0] to-[#C8959F]';
    }
  };

  const getCategoryBadge = (category: string) => {
    const cat = categories.find(c => c.id === category);
    return cat ? cat.label : 'General';
  };

  return (
    <section id="faq" className="py-24 md:py-32 bg-gradient-to-b from-white via-[#FDF5F6] to-white relative overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-br from-[#D9A7B0]/10 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-tr from-[#E8B4BC]/10 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-[#D9A7B0]/5 via-transparent to-[#E8B4BC]/5 rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-[1200px] mx-auto px-6 relative">
        {/* Header Section */}
        <div className="text-center mb-16">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#D9A7B0]/20 to-[#E8B4BC]/20 rounded-full border border-[#D9A7B0]/30 mb-6">
            <div className="relative w-2 h-2 bg-[#D9A7B0] rounded-full">
              <div className="absolute inset-0 bg-[#D9A7B0] rounded-full animate-ping opacity-75"></div>
            </div>
            <span className="text-sm font-semibold text-[#C8959F] uppercase tracking-wide">
              Got Questions?
            </span>
          </div>

          <h2 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-[#2B6777] mb-6 tracking-tight">
            Questions? We've Got Answers.
          </h2>
          <p className="text-lg md:text-xl text-[#2B6777]/70 max-w-[700px] mx-auto leading-relaxed">
            Everything you need to know about window replacement in Virginia—from pricing to process.
          </p>
        </div>

        {/* Category Filter Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => {
                setActiveCategory(category.id);
                setOpenIndex(null);
              }}
              className={`inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap ${
                activeCategory === category.id
                  ? 'bg-gradient-to-r from-[#D9A7B0] to-[#C8959F] text-white shadow-lg shadow-[#D9A7B0]/30 scale-105'
                  : 'bg-white text-[#2B6777]/70 hover:bg-[#D9A7B0]/10 hover:text-[#C8959F] border border-[#D9A7B0]/20'
              }`}
            >
              <i className={`${category.icon} text-lg`}></i>
              {category.label}
            </button>
          ))}
        </div>

        {/* FAQ Count */}
        <div className="text-center mb-8">
          <span className="text-sm text-[#2B6777]/50">
            Showing {filteredFaqs.length} question{filteredFaqs.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* FAQ List */}
        <div className="max-w-[900px] mx-auto space-y-4">
          {filteredFaqs.map((faq, index) => (
            <div 
              key={index}
              className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-[#D9A7B0]/10 hover:border-[#D9A7B0]/30"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-5 flex items-start justify-between text-left cursor-pointer hover:bg-gradient-to-r hover:from-[#D9A7B0]/5 hover:to-transparent transition-all duration-300"
              >
                <div className="flex-1 pr-4">
                  {/* Category Badge */}
                  {activeCategory === 'all' && (
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold text-white bg-gradient-to-r ${getCategoryColor(faq.category)} mb-3`}>
                      {getCategoryBadge(faq.category)}
                    </span>
                  )}
                  <h4 className="text-lg font-heading font-semibold text-[#2B6777] leading-snug group-hover:text-[#C8959F] transition-colors">
                    {faq.question}
                  </h4>
                </div>
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
                  openIndex === index 
                    ? 'bg-gradient-to-r from-[#D9A7B0] to-[#C8959F] rotate-180' 
                    : 'bg-[#D9A7B0]/10 group-hover:bg-[#D9A7B0]/20'
                }`}>
                  <i className={`ri-arrow-down-s-line text-2xl ${openIndex === index ? 'text-white' : 'text-[#C8959F]'}`}></i>
                </div>
              </button>
              
              <div className={`overflow-hidden transition-all duration-300 ${openIndex === index ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="px-6 pb-6 pt-2">
                  <div className="pl-0 border-l-4 border-[#D9A7B0]/30 pl-4">
                    <p className="text-base text-[#2B6777]/70 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <div className="inline-flex flex-col items-center gap-4 bg-gradient-to-br from-white to-[#FDF5F6] rounded-3xl p-8 shadow-lg border border-[#D9A7B0]/20">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-r from-[#D9A7B0] to-[#C8959F] flex items-center justify-center shadow-lg">
              <i className="ri-question-answer-line text-white text-3xl"></i>
            </div>
            <h3 className="text-xl font-heading font-semibold text-[#2B6777]">
              Still have questions?
            </h3>
            <p className="text-[#2B6777]/60 max-w-[400px]">
              We're here to help. Get in touch and we'll respond within 24 hours.
            </p>
            <a
              href="mailto:Snapwindowsai@gmail.com"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#D9A7B0] to-[#C8959F] text-white text-base font-semibold rounded-xl hover:shadow-xl hover:shadow-[#D9A7B0]/30 transition-all duration-300 cursor-pointer whitespace-nowrap hover:scale-105"
            >
              Contact Us
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
